import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_ORGANIZACAO_E_CONTA } from "@/lib/ajuda/dialogos/organizacao-e-conta";

type Resumo = {
  email: string;
  temSenha: boolean;
  organizacoes: { id: string; name: string; isPersonal: boolean; outrosMembros: number; pedidosComLaboratorio: number }[];
};

/** Confirma o pedido de reset ou exclusão: mostra o que vai acontecer e pede a senha (ou o e-mail,
 *  pra conta só com Google/Microsoft). */
export function PedidoLgpdDialog({
  tipo,
  resumo,
  onFechar,
  onFeito,
}: {
  tipo: "RESET_DADOS" | "EXCLUSAO" | null;
  resumo: Resumo;
  onFechar: () => void;
  onFeito: () => void;
}) {
  const [segredo, setSegredo] = useState("");
  const [motivo, setMotivo] = useState("");
  const fechar = () => {
    setSegredo("");
    setMotivo("");
    onFechar();
  };
  const pedir = trpc.privacidade.pedir.useMutation({
    onSuccess: () => {
      toast.success("Pedido enviado. Você recebe um e-mail quando for atendido.");
      onFeito();
      fechar();
    },
    onError: (e) => toast.error(e.message),
  });
  const exclusao = tipo === "EXCLUSAO";
  const comEquipe = resumo.organizacoes.filter((o) => o.outrosMembros > 0);
  const bloqueado = exclusao && comEquipe.length > 0;
  const comLaboratorio = resumo.organizacoes.reduce((t, o) => t + o.pedidosComLaboratorio, 0);

  return (
    <Dialog open={!!tipo} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={exclusao ? AJUDA_ORGANIZACAO_E_CONTA.pedidoDeExclusao : AJUDA_ORGANIZACAO_E_CONTA.pedidoDeReset}>
            {exclusao ? "Pedir exclusão da conta" : "Pedir reset dos dados"}
          </DialogTitle>
          <DialogDescription>
            {exclusao
              ? "A conta é apagada, junto com os dados das organizações de que você é dona."
              : "Pacientes, agenda, pedidos, arquivos e estoque das organizações de que você é dona são apagados. Conta, organizações, equipe e configurações ficam."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {resumo.organizacoes.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Organizações atingidas</p>
              <ul className="mt-1 list-disc pl-5 text-zinc-700 dark:text-foreground">
                {resumo.organizacoes.map((o) => (
                  <li key={o.id}>
                    {o.isPersonal ? `${o.name} (pessoal)` : o.name}
                    {o.outrosMembros > 0 ? ` — ${o.outrosMembros} outro${o.outrosMembros > 1 ? "s" : ""} na equipe` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {comLaboratorio > 0 && (
            <p className="text-[12.5px] text-zinc-600 dark:text-muted-foreground">
              {comLaboratorio} pedido{comLaboratorio > 1 ? "s" : ""} de prótese já recebido{comLaboratorio > 1 ? "s" : ""} por laboratório
              fica{comLaboratorio > 1 ? "m" : ""}, com o paciente anonimizado.
            </p>
          )}
          {bloqueado ? (
            <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[12.5px] text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Antes de excluir a conta, transfira a propriedade de {comEquipe.map((o) => o.name).join(", ")} em Configurações ›
              Propriedade. A equipe não pode perder a organização junto com a sua conta.
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="lgpd-segredo">{resumo.temSenha ? "Sua senha" : `Digite o e-mail da conta (${resumo.email})`}</Label>
                <Input id="lgpd-segredo" type={resumo.temSenha ? "password" : "email"} value={segredo} onChange={(e) => setSegredo(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lgpd-motivo">Motivo (opcional)</Label>
                <Input id="lgpd-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={fechar} className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50">
            Cancelar
          </button>
          {!bloqueado && (
            <button
              type="button"
              disabled={!segredo || pedir.isPending}
              onClick={() =>
                tipo &&
                pedir.mutate({
                  tipo,
                  ...(resumo.temSenha ? { senha: segredo } : { emailDeConfirmacao: segredo }),
                  motivo: motivo || undefined,
                })
              }
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {pedir.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Enviar pedido
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
